import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';

import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import maxBy from 'lodash/maxBy';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import orderBy from 'lodash/orderBy';
import every from 'lodash/every';
import range from 'lodash/range';
import get from 'lodash/get';
import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createFilters from '../../shared/filters';
import createSwitchControlMulti from '../../shared/switch-control/multi';
import getRequestQuery from '../shared/get-request-query';
import createTable from '../components/table/index';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';

const table = createTable({
	namespace: 'chainList',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

const calendar = createCalendar({
	namespace: 'chainList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(2, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'chainList',
	stateNodeName: 'filters',
	queryBuildFunc: getRequestQuery,
	listOfFilters: [
		{
			name: 'affiliate_ids',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.CHAINS.FILTERS.AFFILIATES
			}
		},
		{
			name: 'offer',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.CHAINS.FILTERS.OFFERS
			}
		},
		{
			name: 'landing',
			title: 'Landings',
			placeholder: 'Add landings',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.CHAINS.FILTERS.LANDINGS
			}
		},
		{
			name: 'transit',
			title: 'Transits',
			placeholder: 'Add transits',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.CHAINS.FILTERS.TRANSITS
			}
		},
		{
			name: 'country',
			title: 'Countries',
			placeholder: 'Add countries',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.CHAINS.FILTERS.COUNTRIES
			}
		},
		{
			name: 'internal_status',
			title: 'Statuses',
			placeholder: 'Add statuses',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: true,
			options: {
				type: 'dynamic',
				source: endpoints.CHAINS.FILTERS.STATUSES
			}
		}
	]
});

const switchControlMultiStatus = createSwitchControlMulti({
	namespace: 'chainList',
	stateNodeName: 'switchControlMultiStatus',
	defaultValues: [
		{ name: 'all', placeholder: 'All', active: false },
		{ name: 'active', placeholder: 'Active', active: true, className: 'bullet-two' },
		{ name: 'hidden', placeholder: 'Hidden', active: true, className: 'bullet-three' },
		{ name: 'archived', placeholder: 'Archived', active: false, className: 'bullet-four' }
	]
});

// actions types
const TOGGLE_PROGRESS_BAR = 'chainList/toggle-progress-bar';
const FETCH_DATA_SUCCESS = 'chainList/fetch-data-success';
const FETCH_DATA_ERROR = 'chainList/fetch-data-error';
// const SORT_BY_COLUMN = 'chainList/table-sort-by-column';
const SHOW_MORE_ROWS = 'chainList/table-show-more-rows';
const REMOVE_SUCCESS = 'chainList/remove-success';
const REMOVE_ERROR = 'chainList/remove-error';
const SHOW_PROGRESS_BAR = 'chainList/show-progress-bar';
const CLEAR = 'chainList/clear';
const SET_SEARCH_QUERY = 'chainList/set-search-query';
const TOGGLE_OFFER = 'chainList/toggle-offer';
const TOGGLE_OFFERS_VIEW = 'chainList/toggle-offers-view';
const UPDATE_CHAIN_DATE_SUCCESS = 'chainList/update-chain-date-success';
const UPDATE_CHAIN_DATE_ERROR = 'chainList/update-chain-date-error';
const GOTO_PAGE_SUCCESS = 'chainList/goto-page-success';

const APPLY_QUICK_FILTER = 'chainList/apply-quick-filter';

const SWITCH_TO_ORDER = 'chainList/switch-to-order';
const ON_ORDER_SORT_END = 'chainList/on-order-sort-end';
const SAVE_ORDER_SUCCESS = 'chainList/save-order-success';
const SAVE_ORDER_ERROR = 'chainList/save-order-error';

// permissions
export const allowedPermissions = {
	'view': 'chains_view',
	'edit': 'chains_update',
	'create': 'chains_create'
};

// initial state
const initialState = {
	data: null,
	query: {
		groups: ['date'],
		date_range: {
			start: null,
			end: null
		}
	},
	initialListOfOffers: null,
	table: table.initialState,
	showProgressBar: false,
	filters: filters.initialState,
	search: '',
	shouldSwitchToOrderMode: false,
	calendar: calendar.initialState,
	sortedOfferList: [],
	collapseAllOffers: true,
	quickFilters: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'new', placeholder: 'New', active: false },
		{ name: 'top', placeholder: 'Top', active: false },
		{ name: 'recommended', placeholder: 'Recommended', active: false },
		{ name: 'common', placeholder: 'Common', active: false }
	],
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: 0
	},
	switchControlMultiStatus: switchControlMultiStatus.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'chainList');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`chaining/?${qs.stringify(savedFilters)}`));
			}

			dispatch(request(getState, externalQuery && externalQuery.query)).then(() => {
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},
		updateDate: options => dispatch => {
			const { id, updated_at, shouldUpdate } = options;
			const url = `${endpoints.CHAINS.BASE}/${id}`;
			const request = http(
				{
					url,
					method: 'put',
					actions: [
						UPDATE_CHAIN_DATE_SUCCESS,
						UPDATE_CHAIN_DATE_ERROR
					],
					data: {
						id,
						shouldUpdate,
						updated_at
					}
				}
			);
			return request(dispatch);
		},

		remove: id => dispatch => {
			const request = http({
				url: `${endpoints.OFFERS.BASE}/${id}`,
				method: 'delete',
				actions: [
					REMOVE_SUCCESS,
					REMOVE_ERROR
				]
			});

			dispatch(actions.toggleProgressBar());

			request(dispatch);
		},

		applyQuickFilter: filterName => dispatch => {
			dispatch(actions.toggleProgressBar());
			runActionsWithDelay(() => dispatch({ type: APPLY_QUICK_FILTER, payload: filterName }));
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		showMoreRows: () => ({
			type: SHOW_MORE_ROWS
		}),

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(getState));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('chainSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		apply: () => (dispatch, getState) => {
			dispatch(actions.showProgressBar());
			dispatch(actions.setSearchQuery());
			dispatch(request(getState));
		},

		clear: () => dispatch => {
			dispatch(replace('chaining/'));
			dispatch({
				type: CLEAR
			});
		},
		toggleSwitchToOrder: () => ({
			type: SWITCH_TO_ORDER
		}),

		orderSortedEnd: sorted => ({
			type: ON_ORDER_SORT_END,
			payload: sorted
		}),

		saveOrderSort: () => (dispatch, getState) => {
			const request = http({
				url: endpoints.OFFERS.SORT,
				method: 'post',
				actions: [SAVE_ORDER_SUCCESS, SAVE_ORDER_ERROR],
				data: {
					data: getState().programsList.sortedOfferList
				}
			});

			dispatch(actions.showProgressBar());

			dispatch(request).then(action => {
				if (action.type === SAVE_ORDER_SUCCESS) {
					dispatch(actions.clear());
				}
			});
		},
		toggleRow: id => dispatch => {
			dispatch({
				type: TOGGLE_OFFER,
				payload: id
			});
		},
		toggleOffersView: () => dispatch => {
			dispatch({ type: TOGGLE_OFFERS_VIEW });
		},
		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			request(getState)(dispatch);
		}
	},
	table.actions,
	filters.actions,
	switchControlMultiStatus.actions,
	calendar.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('programs') > -1) {
				return {
					...state,
					data: null,
					collapseAllOffers: true,
					pagination: {
						...state.pagination,
						currentPage: initialState.pagination.currentPage
					}
				};
			}
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			let data = action.payload.data;
			data = data.map(row => {
				row.geo = getGeo(row);
				row.languages = getLanguages(row);
				row.collapsed = true;
				return row;
			});
			return {
				...state,
				data,
				initialListOfOffers: !state.initialListOfOffers ? data : state.initialListOfOffers,
				showProgressBar: false,
				pagination: {
					...state.pagination,
					total: Number(get(action, ['payload', 'data', '0', 'total_count'], 0))
				},
				error: false
			};
		},
		[UPDATE_CHAIN_DATE_SUCCESS]: (state, action) => ({
			...state,
			data: null,
			showProgressBar: false,
			error: false
		}),
		[UPDATE_CHAIN_DATE_ERROR]: (state, action) => ({
			...state,
			showProgressBar: false,
			error: true
		}),
		[APPLY_QUICK_FILTER]: (state, action) => ({
			...state,
			data: null,
			showProgressBar: false,
			quickFilters: state.quickFilters.map(filter => {
				return filter.name === action.payload ? { ...filter, active: true } : { ...filter, active: false };
			})
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[REMOVE_SUCCESS]: state => ({ ...state, data: null, showProgressBar: false }),
		[REMOVE_ERROR]: state => ({ ...state, error: true }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SHOW_MORE_ROWS]: state => ({
			...state,
			table: {
				...state.table,
				rowsNumber: state.table.rowsNumber += initialState.table.rowsNumber
			}
		}),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload }),
		[SHOW_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[CLEAR]: state => ({ ...initialState }),
		[SWITCH_TO_ORDER]: state => ({
			...state,
			shouldSwitchToOrderMode: !state.shouldSwitchToOrderMode
		}),
		[ON_ORDER_SORT_END]: (state, actions) => ({
			...state,
			sortedOfferList: actions.payload
		}),
		[SAVE_ORDER_SUCCESS]: state => ({
			...state,
			shouldSwitchToOrderMode: false,
			sortedOfferList: []
		}),
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[TOGGLE_OFFER]: (state, action) => {
			const id = action.payload;
			let data = state.data;
			data = data.map(item => {
				if (item.id === id) {
					item.collapsed = !item.collapsed;
				}
				return item;
			});
			let allOpened = every(data, { collapsed: false });
			return {
				...state,
				data,
				collapseAllOffers: !allOpened
			};
		},
		[TOGGLE_OFFERS_VIEW]: state => {
			let data = state.data;
			data = data.map(offer => {
				offer.collapsed = !state.collapseAllOffers;
				return offer;
			});
			return {
				...state,
				data,
				collapseAllOffers: !state.collapseAllOffers
			};
		}
	},
	filters.reducers,
	table.reducers,
	switchControlMultiStatus.reducers,
	calendar.reducers,
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.chainList.data,
	getIntl: state => intl(state),
	getProgressBar: state => state.chainList.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getTable: table.selectors.getTable,
	getLanguages: state => state.root.lists.languages,
	getNeedToUpdateChains: state => state.root.needToUpdateChainsCount,
	getOrderOfferList: state => {
		if (!state.chainList.initialListOfOffers) return [];

		const { initialListOfOffers } = state.chainList;

		return orderBy(initialListOfOffers, 'display_priority', 'desc');
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.chainList.filters,
		state => state
	)(state),
	getQuickFilters: state => state.chainList.quickFilters,
	getSearchInitialValues: state => ({
		search: state.chainList.search
	}),
	getCalendar: state => calendar.selectors.getCalendar(state => state.chainList.calendar, state => state)(state),
	getSwitchControlMultiStatusItems: state => switchControlMultiStatus.selectors.getControls(state),
	getSwitchToOrderMode: state => state.chainList.shouldSwitchToOrderMode,
	getShouldShowSaveOrderBtn: state => !!state.chainList.sortedOfferList.length,
	getAllOffersCollapsed: state => state.chainList.collapseAllOffers,
	getError: state => state.chainList.error,
	getPagination: state => {
		const { perPage, total, currentPage } = state.chainList.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	}
};

const getGeo = row => {
	return row.locations && row.locations.length && row.locations.map(location => {
		let usd = filter(row.payouts, { location_id: location.id, currency: 'usd' });
		usd = usd && usd.length && maxBy(usd, payout => payout.amount).amount || null;
		let rub = filter(row.payouts, { location_id: location.id, currency: 'rub' });
		rub = rub && rub.length && maxBy(rub, payout => payout.amount).amount || null;

		location.payout_usd = usd;
		location.payout_rub = rub;
		return location;
	}) || [];
};

const getLanguages = row => {
	return row.domains && row.domains.length && row.domains.reduce((res, row) => {
		row.languages.forEach(lang => {
			if (res.indexOf(lang) === -1) {
				res.push(lang);
			}
		});
		return res;
	}, []) || [];
};

function request(getState, externalQuery = {}) {
	const {
		search, calendar, switchControlMultiStatus, quickFilters, table: { sortByColumn },
		filters: { selectedFilters },
		pagination: { perPage, currentPage }
		// table: { sortByColumn }
	} = getState().chainList;
	const activeQuickFiltersStatus = switchControlMultiStatus
		.filters.filter(f => f.active && f.name != 'all').map(f => f.name);
	const activeQuickFiltersMark = quickFilters.filter(f => f.active && f.name != 'all').map(f => f.name);

	const limit = perPage;
	const offset = (currentPage - 1) * perPage;

	let filters = {};
	const inverseFilters = {};

	if (selectedFilters && !isEmpty(selectedFilters)) {
		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});
	}

	if (externalQuery && externalQuery.query && externalQuery.query.filters) {

		externalQuery.entity.filters && externalQuery.entity.filters.forEach(filter => {
			if (filter.is_inverted && JSON.parse(filter.is_inverted)) {
				inverseFilters[filter.name] = filter.values.map(f => f.value);
			}
		});

		externalQuery.query.filters && forEach(externalQuery.query.filters, (value, key) => {
			if (inverseFilters[key]) {
				return;
			}
			filters[key] = value;
		});
	}

	const query = qs.stringify({
		offset,
		limit,
		quick_filters: {
			mark: activeQuickFiltersMark || '',
			status: activeQuickFiltersStatus || ''
		},
		filters: filters,
		inverse_filters: inverseFilters,
		date_range: getDateRangeFromCalendar(calendar),
		search,
		sort: [sortByColumn.index, sortByColumn.direction]
	});

	return http({
		url: `${endpoints.CHAINS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
}

const runActionsWithDelay = actionFn => setTimeout(actionFn);




// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/chaining/list/index.js